import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, History } from "lucide-react";
import { api } from "@/lib/api";
import { Pager } from "@/components/admin/Pager";
import { saveBlob } from "@/components/admin/PointsUploadPanel";
import { fmtNum, fmtDateTime } from "@/lib/format";

// Registro (log) de cargas masivas confirmadas: quién, cuándo, archivo, resultado y detalle descargable.
export function ImportLog({ kind, refreshKey = 0, title = "Historial de cargas" }: { kind: "puntos" | "cedulas" | "debito_envio" | "debito_resultado"; refreshKey?: number; title?: string }) {
  const debit = kind.startsWith("debito");
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFailed(false);
    api.get("/admin/imports", { params: { kind, page, page_size: 10 } }).then((r) => setData(r.data)).catch(() => setFailed(true));
  }, [kind, page, refreshKey]);

  const download = async (imp: any) => {
    try {
      const { data: blob } = await api.get(`/admin/imports/${imp.id}/download`, { responseType: "blob" });
      saveBlob(blob, `carga_${kind}_${(imp.created_at || "").slice(0, 10)}.csv`);
    } catch { toast.error("No se pudo descargar el detalle de la carga"); }
  };

  return (
    <section className="card-obsidian overflow-hidden" aria-labelledby={`import-log-${kind}`} data-testid={`import-log-${kind}`}>
      <div className="px-5 pt-5 pb-3">
        <h2 id={`import-log-${kind}`} className="text-lg font-semibold flex items-center gap-2">
          <History className="w-5 h-5 text-slate-500" aria-hidden="true" /> {title}
        </h2>
        <p className="text-sm text-slate-600">{kind === "debito_envio" ? "Cada archivo generado para Armony queda registrado con los cobros que incluyó." : "Cada carga confirmada queda registrada. Descarga el detalle para ver el resultado de cada fila del archivo."}</p>
      </div>
      {failed ? (
        <p className="px-5 pb-5 text-sm text-red-700">No pudimos cargar el historial. Recarga la página para reintentar.</p>
      ) : !data ? (
        <p className="px-5 pb-5 text-sm text-slate-600" role="status">Cargando…</p>
      ) : data.items.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-slate-600">Todavía no hay cargas registradas.</p>
      ) : (
        <>
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Historial de cargas">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-y border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Fecha</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Por</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Archivo</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">{kind === "debito_envio" ? "Registros" : "Filas"}</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">{kind === "debito_envio" ? "Cobros" : kind === "debito_resultado" ? "Aplicadas" : "Cargadas"}</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Con error</th>
                  {kind === "puntos" && <th scope="col" className="text-right font-medium px-5 py-3">Puntos</th>}
                  {kind === "puntos" && <th scope="col" className="text-left font-medium px-5 py-3">Motivo general</th>}
                  {debit && <th scope="col" className="text-left font-medium px-5 py-3">Resumen</th>}
                  <th scope="col" className="px-5 py-3"><span className="sr-only">Detalle</span></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((imp: any) => (
                  <tr key={imp.id} className="border-b border-slate-200 last:border-0" data-testid="import-log-row">
                    <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDateTime(imp.created_at)}</td>
                    <td className="px-5 py-3 text-slate-700">{imp.actor_name || "—"}<p className="text-xs text-slate-600">{imp.actor_email}</p></td>
                    <td className="px-5 py-3 text-slate-700 font-mono text-xs break-all">{imp.filename || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtNum(imp.total_rows)}</td>
                    <td className="px-5 py-3 text-right font-mono text-emerald-800">{fmtNum(imp.applied_rows)}</td>
                    <td className={`px-5 py-3 text-right font-mono ${imp.error_rows ? "text-red-700" : "text-slate-600"}`}>{fmtNum(imp.error_rows)}</td>
                    {kind === "puntos" && <td className="px-5 py-3 text-right font-mono">{fmtNum(imp.points_total)}</td>}
                    {kind === "puntos" && <td className="px-5 py-3 text-slate-700">{imp.motivo || "Por fila"}</td>}
                    {debit && <td className="px-5 py-3 text-slate-700">{imp.motivo || "—"}</td>}
                    <td className="px-5 py-3 text-right">
                      <button type="button" onClick={() => download(imp)} className="text-sm text-navy hover:underline inline-flex items-center gap-1" data-testid="import-log-download">
                        <Download className="w-3.5 h-3.5" aria-hidden="true" /> Detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </>
      )}
    </section>
  );
}
